import {
  ensureJson,
  generateGuid,
  getProviderFactory,
  IAuthenticationInfo,
  IAuthProvider,
  IProviderFactory,
  RestClient,
  SBusinessError,
} from 'slipstream-components';
import myProfileAPI from 'src/plugins/ui-administration/src/apis/MyProfileAPI';
import customerAPI from '../../apis/CustomerAPI';
import iamAPI from '../../apis/IamAPI';
import sseAPI from '../../apis/SseAPI';

export default class DefaultAuthProvider implements IAuthProvider<DefaultAuthenticationInfo> {
  private providerFactory: IProviderFactory;
  private _TOKEN_KEY: string;
  private _FEATURE_KEY: string;
  private _ROLE_SCOPE_KEY: string;
  private _TOKEN_ISSUE_AT_KEY: string;
  private _TOKEN_EXPIRE_AT_KEY: string;
  private _USER_ID_KEY: string;
  private _USER_NAME_KEY: string;
  private _TENANT_ID_KEY: string;
  private _TENANT_KEY: string;
  private _TOKEN_LOCAL_TIME_KEY: string;
  private _TENANT_PROPERTIES_KEY: string;
  private _PROJECTS_KEY: string;
  private _SSE_TOKEN_KEY: string;
  private _CUSTOMER_ID_KEY: string;
  private _CUSTOMER_KEY: string;
  private _CAPABILITIES_KEY: string;
  private _LOGIN_ROLE_SCOPE_KEY: string;
  private _TOKEN_DETAIL_KEY: string;
  private _USER_INFO_KEY: string;

  private token: string;
  private tenantProperties: any;
  private userId: string;
  private userName: string;
  private features: string[];
  private roleScope: string;
  private tokenIssueAt: string;
  private tokenExpireAt: string;
  private tenantId: string;
  private tenant: string;
  private sseToken: string;
  private tokenLocalTime: number;
  private projects: IProject[];
  private customerId: string;
  private customer: string;
  private capabilities: string[];
  private authenticationMode: string;
  private loginRoleScope: string;
  private tokenDetail: any;
  private userInfo: any;

  constructor(providerFactory: IProviderFactory) {
    this.providerFactory = providerFactory;
    const systemId = this.providerFactory.getSystemId();
    this._TOKEN_KEY = systemId + '_TOKEN';
    this._TOKEN_DETAIL_KEY = systemId + '_TOKEN_DETAIL';
    this._FEATURE_KEY = systemId + '_FEATURE';
    this._ROLE_SCOPE_KEY = systemId + '_ROLE_SCOPE';
    this._TOKEN_EXPIRE_AT_KEY = systemId + '_TOKEN_EXPIRE_AT';
    this._TOKEN_ISSUE_AT_KEY = systemId + '_TOKEN_ISSUE_AT';
    this._USER_ID_KEY = systemId + '_USER_ID';
    this._USER_NAME_KEY = systemId + '_USER_NAME';
    this._TENANT_ID_KEY = systemId + '_TENANT_ID';
    this._TENANT_KEY = systemId + '_TENANT';
    this._TOKEN_LOCAL_TIME_KEY = systemId + '_TOKEN_LOCAL_TIME';
    this._TENANT_PROPERTIES_KEY = systemId + '_TENANT_PROPERTIES';
    this._PROJECTS_KEY = systemId + '_PROJECTS';
    this._SSE_TOKEN_KEY = systemId + '_SSE_TOKEN';
    this._CUSTOMER_ID_KEY = systemId + '_CUSTOMER_ID';
    this._CUSTOMER_KEY = systemId + '_CUSTOMER';
    this._CAPABILITIES_KEY = systemId + '_CAPABILITIES';
    this._LOGIN_ROLE_SCOPE_KEY = systemId + '_LOGIN_ROLE_SCOPE';
    this._USER_INFO_KEY = systemId + '_USER_INFO';
  }

  public setProjects(projects: any) {
    this.projects = projects;
    sessionStorage.setItem(this._PROJECTS_KEY, JSON.stringify(projects));
  }

  public getProjects(): IProject[] {
    if (this.projects == null) {
      const projectStr = sessionStorage.getItem(this._PROJECTS_KEY);
      this.projects = JSON.parse(projectStr);
    }
    return this.projects;
  }

  public setRoleScope(roleScope: any) {
    this.roleScope = roleScope;
    sessionStorage.setItem(this._ROLE_SCOPE_KEY, roleScope);
  }

  public getRoleScope(): 'Enterprise' | 'OpCo' | 'SP' {
    if (this.roleScope == null) {
      this.roleScope = sessionStorage.getItem(this._ROLE_SCOPE_KEY);
    }
    return this.roleScope as 'Enterprise' | 'OpCo' | 'SP';
  }

  public setLoginRoleScope(roleScope: any) {
    this.loginRoleScope = roleScope;
    sessionStorage.setItem(this._LOGIN_ROLE_SCOPE_KEY, roleScope);
  }

  public getLoginRoleScope() {
    if (!this.loginRoleScope) {
      this.loginRoleScope = sessionStorage.getItem(this._LOGIN_ROLE_SCOPE_KEY);
    }
    return this.loginRoleScope;
  }

  /**
   * It only indicates if the tenant is switched from sp admin to tenant admin. Switching from tenant to tenant will not be counted in.
   */
  public tenantSwitched() {
    if (
      this.roleScope === 'Enterprise' &&
      (this.loginRoleScope === 'SP' || this.loginRoleScope === 'OpCo')
    ) {
      return true;
    }
    return false;
  }

  public setTokenIssueAt(issuedAt: string) {
    this.tokenIssueAt = issuedAt;
    sessionStorage.setItem(this._TOKEN_ISSUE_AT_KEY, issuedAt);
  }

  public getTokenIssueAt() {
    if (this.tokenIssueAt == null) {
      this.tokenIssueAt = sessionStorage.getItem(this._TOKEN_ISSUE_AT_KEY);
    }
    return this.tokenIssueAt;
  }

  public setTokenExpireAt(expireAt: string) {
    this.tokenExpireAt = expireAt;
    sessionStorage.setItem(this._TOKEN_EXPIRE_AT_KEY, expireAt);
  }

  public getTokenExpireAt() {
    if (this.tokenExpireAt == null) {
      this.tokenExpireAt = sessionStorage.getItem(this._TOKEN_EXPIRE_AT_KEY);
    }
    return this.tokenExpireAt;
  }

  public setUserId(userId: string) {
    this.userId = userId;
    sessionStorage.setItem(this._USER_ID_KEY, userId);
  }

  public getUserId() {
    if (this.userId == null) {
      this.userId = sessionStorage.getItem(this._USER_ID_KEY);
    }
    return this.userId;
  }

  public setUserName(userName: string) {
    this.userName = userName;
    sessionStorage.setItem(this._USER_NAME_KEY, userName);
  }

  public getUserName() {
    if (this.userName == null) {
      this.userName = sessionStorage.getItem(this._USER_NAME_KEY);
    }
    return this.userName;
  }

  public getUserInfo() {
    if (this.userInfo == null) {
      this.userInfo = sessionStorage.getItem(this._USER_INFO_KEY);
    }
    return this.userInfo;
  }

  public setTenantId(tenantId: string) {
    this.tenantId = tenantId;
    sessionStorage.setItem(this._TENANT_ID_KEY, tenantId);
  }

  public getTenantId() {
    if (this.tenantId == null) {
      this.tenantId = sessionStorage.getItem(this._TENANT_ID_KEY);
    }
    return this.tenantId;
  }

  public getIdentifier() {
    return this.getTenantId();
  }

  public getTenantUuid() {
    const id = this.getTenantId();
    return (
      id.substring(0, 8) +
      '-' +
      id.substring(8, 12) +
      '-' +
      id.substring(12, 16) +
      '-' +
      id.substring(16, 20) +
      '-' +
      id.substring(20)
    );
  }

  public setTenant(tenant: string) {
    this.tenant = tenant;
    sessionStorage.setItem(this._TENANT_KEY, tenant);
  }

  public getTenant() {
    if (this.tenant == null) {
      this.tenant = sessionStorage.getItem(this._TENANT_KEY);
    }
    return this.tenant;
  }

  public setSseToken(token: string) {
    this.sseToken = token;
    sessionStorage.setItem(this._SSE_TOKEN_KEY, token);
  }

  public getSseToken() {
    if (this.sseToken == null) {
      this.sseToken = sessionStorage.getItem(this._SSE_TOKEN_KEY);
    }
    return this.sseToken;
  }

  public setCustomer(customer: string) {
    this.customer = customer;
    sessionStorage.setItem(this._CUSTOMER_KEY, customer);
  }

  public getCustomer() {
    if (this.customer == null) {
      this.customer = sessionStorage.getItem(this._CUSTOMER_KEY);
    }
    return this.customer;
  }

  public setCustomerId(customerId: string) {
    this.customerId = customerId;
    sessionStorage.setItem(this._CUSTOMER_ID_KEY, customerId);
  }

  public getCustomerId() {
    if (this.customerId == null) {
      this.customerId = sessionStorage.getItem(this._CUSTOMER_ID_KEY);
    }
    return this.customerId;
  }

  public setCapabilities(capabilities: string[]) {
    this.capabilities = capabilities;
    if (this.capabilities == null) {
      sessionStorage.setItem(this._CAPABILITIES_KEY, '');
    } else {
      sessionStorage.setItem(this._CAPABILITIES_KEY, JSON.stringify(capabilities));
    }
  }

  public getCapabilities() {
    if (this.capabilities == null) {
      this.capabilities = JSON.parse(sessionStorage.getItem(this._CAPABILITIES_KEY));
    }
    return this.capabilities;
  }

  public setTokenLocalTime(tokenLocalTime: number) {
    this.tokenLocalTime = tokenLocalTime;
    if (tokenLocalTime == null) {
      sessionStorage.setItem(this._TOKEN_LOCAL_TIME_KEY, '');
    } else {
      sessionStorage.setItem(this._TOKEN_LOCAL_TIME_KEY, '' + tokenLocalTime);
    }
  }

  public getTokenLocalTime() {
    if (this.tokenLocalTime == null) {
      const tokenTime = sessionStorage.getItem(this._TOKEN_LOCAL_TIME_KEY);
      if (tokenTime == null) {
        return null;
      } else {
        this.tokenLocalTime = parseInt(tokenTime, 10);
      }
    }
    return this.tokenLocalTime;
  }

  public getTenantProperties(): { property: [{ [key: string]: any }] } {
    if (this.tenantProperties == null) {
      this.tenantProperties = JSON.parse(sessionStorage.getItem(this._TENANT_PROPERTIES_KEY));
    }
    return this.tenantProperties;
  }

  public setTenantProperties(props: any) {
    this.tenantProperties = props;
    if (props == null) {
      sessionStorage.setItem(this._TENANT_PROPERTIES_KEY, '');
    } else {
      sessionStorage.setItem(this._TENANT_PROPERTIES_KEY, JSON.stringify(props));
    }
  }

  public getFeatures() {
    if (this.features == null) {
      const featureStr = sessionStorage.getItem(this._FEATURE_KEY);
      if (featureStr == null || featureStr === '') {
        this.features = [];
      }
      this.features = JSON.parse(featureStr);
    }
    return this.features;
  }

  public setFeatures(features: string[]) {
    this.features = features;
    if (features != null) {
      sessionStorage.setItem(this._FEATURE_KEY, JSON.stringify(features));
    }
  }

  public setToken(token: string): any {
    RestClient.setup({
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Token': arguments[0],
        'X-Request-Id': 'UI_' + generateGuid(),
      },
      beforeSend: (url: string, method: string, body: any, headers: any) => {
        if (headers) {
          headers['X-Request-Id'] = 'UI_' + generateGuid();
        }
      },
    });

    // RestClient.onError(function() {
    //     //dispatch
    //     //Jx.MessageHub.publish("slipstream-components.authentication.token.denied", {});
    // });

    this.token = token;
    sessionStorage.setItem(this._TOKEN_KEY, token);
  }

  public getToken() {
    if (this.token == null) {
      this.token = sessionStorage.getItem(this._TOKEN_KEY);
    }
    return this.token;
  }

  public setTokenDetail(token: any) {
    this.tokenDetail = token;
    if (token) {
      sessionStorage.setItem(this._TOKEN_DETAIL_KEY, JSON.stringify(token));
    }
  }

  public getTokenDetail() {
    if (this.tokenDetail == null) {
      const tokenDetail = sessionStorage.getItem(this._TOKEN_DETAIL_KEY);
      this.tokenDetail = JSON.parse(tokenDetail);
    }
    return this.tokenDetail;
  }

  public async saveScopedToken(token, result) {
    const loginLocalTime = new Date().getTime();
    this.setToken(token);
    this.setTokenLocalTime(loginLocalTime);
    this.setTenant(result.token.project.name);
    this.setTenantId(result.token.project.id);
    this.setUserName(result.token.user.name);
    this.setUserId(result.token.user.id);
    this.setTokenExpireAt(result.token.expires_at);
    this.setTokenIssueAt(result.token.issued_at);
    this.setTokenDetail(result.token);
    // var roles = $.map(result.token.roles || [], function(r) {
    //     return r.name;
    // });
    // Jx.Context.roles(roles);
    const roleScope = result.token.roles[0].role_scope;
    this.setRoleScope(roleScope);

    await Promise.all([this.getCustomerInfo(), this.getUserCapabilities()]);

    await this.saveSSEToken();
  }

  public async getCustomerInfo() {
    const result = await customerAPI.getCustomersByParentId(this.getTenantUuid());
    // let custData = await this.restClient.get('/tssm/customer?parent_id=' + this.getTenantUuid() + '&detail=true')
    const custData = result.body;
    const customer = custData.customer[0];

    if (customer != null) {
      this.setCustomer(customer.name);
      this.setCustomerId(customer.uuid);
      const scenario = customer.supported_deployment_scenario || [];
      const properties = customer.properties || {};
      const topoProp = properties.property.find(t => t.name === 'default-tenant-topology');
      if (topoProp) {
        scenario.push(topoProp.value);
      }
      // Add role scope to features as well to resolve feature/scope all case. It's special for some pages also displayed in global scope.
      scenario.push(this.getRoleScope());
      if (
        getProviderFactory().getGlobalConfig().showIntro &&
        scenario.indexOf('Enterprise') !== -1
      ) {
        scenario.push('showIntro');
      }

      // Check service_level to identify whether the tenant is sdwan advanced or essential
      if (scenario.indexOf('managed_wan_v2') !== -1 && customer.service_level !== 'ESSENTIAL') {
        scenario.push('sdwan_adv');
      }
      this.setFeatures(scenario);

      properties.property = properties.property || [];
      properties.deployment_scenario = customer.deployment_scenario;
      properties.supported_deployment_scenario = customer.supported_deployment_scenario;
      properties.vpn_auth = 'psk';
      properties.service_level = customer.service_level;
      properties.vpn_encrypyion = 'aes-256-gcm';
      properties.user_defined_properties = customer.user_defined_properties;
      if (
        customer.ipsec_vpn_cfg == null ||
        customer.ipsec_vpn_cfg.vpn_authentication == null ||
        !customer.ipsec_vpn_cfg.vpn_authentication.preshared_key_enabled
      ) {
        properties.vpn_auth = 'pki';
      } else {
        properties.vpn_auth = 'psk';
      }

      if (
        customer.ipsec_vpn_cfg != null &&
        customer.ipsec_vpn_cfg.overlay_tunnel_encryption &&
        customer.ipsec_vpn_cfg.overlay_tunnel_encryption.encryption_type
      ) {
        properties.vpn_encrypyion =
          customer.ipsec_vpn_cfg.overlay_tunnel_encryption.encryption_type;
      }

      // CXU-20333 for upgrade case from 3.2 to 3.3. If there is no sla_mgmt or sla_mgmt.value is null, add a default value.
      const appQoE = properties.property.filter(p => p.name === 'sla_mgmt');
      if (appQoE == null || appQoE.length === 0) {
        properties.property.push({ name: 'sla_mgmt', value: 'COARSE' });
      } else if (appQoE[0].value) {
        appQoE.value = 'COARSE';
      }
      this.setTenantProperties(properties);
    } else {
      this.setCustomer(this.getTenant());
      this.setCustomerId(this.getTenantId());
      this.setFeatures([]);
      this.setTenantProperties({});
    }
  }

  /**
   * make sure the context is correctly set after refresh.
   */
  public recoverFromContext() {
    const token = this.getToken();
    if (token != null && token !== '') {
      this.setToken(token);
    }
  }

  public getAuthenticationInfo(): DefaultAuthenticationInfo {
    const userName = this.getUserName();
    const userId = this.getUserId();
    const project = this.getTenant();
    const projectId = this.getTenantId();
    return new DefaultAuthenticationInfo(userName, userId, project, projectId);
  }

  public async authenticate(authData) {
    if (authData.token) {
      // handle unscoped SSO token
      return this.getProjectListAndAuthenticate(authData.token);
    } else {
      authData.userName = authData.userName.trim();
      authData.password = authData.password.trim();
      // use username and password for authentication
      // user 'admin' is not allowed to login to WEB portals
      if (authData.userName === 'admin') {
        throw new Error('Admin user is not allowed to login from UI');
      } else {
        const userName = authData.userName;
        const userProject = localStorage.getItem(userName + '_default_project');
        let auth = this.getPasswordAuthPayload(
          authData.userName,
          authData.password,
          authData.tenant
        );
        if (authData.totp) {
          if (authData.useTotpMethod) {
            auth = this.getPasswordTotpAuthPayload(
              authData.userName,
              authData.password,
              authData.totp,
              authData.tenant || userProject
            );
          } else {
            auth = this.getPasswordEmailAuthPayload(
              authData.userName,
              authData.password,
              authData.totp,
              authData.tenant || userProject
            );
          }
        }
        return this.v3Authentication(auth, true, null);
      }
    }
  }

  public isUserAuthenticated() {
    return this.getUserId() != null;
  }

  public async unauthenticate() {
    sessionStorage.removeItem(this._TOKEN_KEY);
    sessionStorage.removeItem(this._TOKEN_KEY);
    sessionStorage.removeItem(this._FEATURE_KEY);
    sessionStorage.removeItem(this._ROLE_SCOPE_KEY);
    sessionStorage.removeItem(this._TOKEN_ISSUE_AT_KEY);
    sessionStorage.removeItem(this._TOKEN_EXPIRE_AT_KEY);
    sessionStorage.removeItem(this._USER_ID_KEY);
    sessionStorage.removeItem(this._USER_NAME_KEY);
    sessionStorage.removeItem(this._TENANT_ID_KEY);
    sessionStorage.removeItem(this._TENANT_KEY);
    sessionStorage.removeItem(this._TOKEN_LOCAL_TIME_KEY);
    sessionStorage.removeItem(this._TENANT_PROPERTIES_KEY);
    sessionStorage.removeItem(this._PROJECTS_KEY);
    sessionStorage.removeItem(this._SSE_TOKEN_KEY);
    sessionStorage.removeItem(this._CUSTOMER_ID_KEY);
    sessionStorage.removeItem(this._CUSTOMER_KEY);
    sessionStorage.removeItem(this._CAPABILITIES_KEY);
    this.invalidToken();
    // if idp initiated then we need to logout the idp session
    /* IDP Logout Code Start */
    const authMode = window.sessionStorage.authenticationMode;
    if (authMode.includes('saml2')) {
      const idpName = window.sessionStorage.idpName;
      this.logoutSSO(idpName);
    }
    /* IDP Logout Code end */

    return true;
  }

  public async invalidToken(token = this.getToken()) {
    const resp = iamAPI.unauthenticate({
      'x-subject-token': token,
    });
  }

  public isAuthenticated() {
    const token = this.getToken();
    const localTime = this.getTokenLocalTime();

    if (token != null && localTime != null) {
      this.setToken(token);
      return true;
    }
    return false;
  }

  public async renewToken(password) {
    const payload = this.getPasswordAuthPayload(this.getUserName(), password, this.getTenant());
    return this.v3Authentication(payload, false);
  }

  public async renewTokenWithTotp(authData) {
    authData.userName = authData.userName.trim();
    authData.password = authData.password.trim();
    // use username and password for authentication
    // user 'admin' is not allowed to login to WEB portals
    const userName = authData.userName;
    const userProject = this.getTenant() || localStorage.getItem(userName + '_default_project');
    let auth = this.getPasswordAuthPayload(authData.userName, authData.password, authData.tenant);
    if (authData.totp) {
      if (authData.useTotpMethod) {
        auth = this.getPasswordTotpAuthPayload(
          authData.userName,
          authData.password,
          authData.totp,
          authData.tenant || userProject
        );
      } else {
        auth = this.getPasswordEmailAuthPayload(
          authData.userName,
          authData.password,
          authData.totp,
          authData.tenant || userProject
        );
      }
    }
    return this.v3Authentication(auth, false, null);
  }

  /**
   * Switch the scope to one tenant. It can only happend when the intial login is SP_Admin
   */
  public async switchProject(input: any) {
    const project = input.project;
    const currentToken = this.getToken();
    const payload = this.getTokenAuthPayload(currentToken, project);
    return this.v3Authentication(payload, true);
  }

  public getAuthenticationMode() {
    if (this.authenticationMode == null) {
      this.authenticationMode = sessionStorage.getItem('authenticationMode');
    }
    return this.authenticationMode;
  }

  public async saveUserInfo(userName, forceClear?: boolean) {
    if ((!this.userInfo && !sessionStorage.getItem(this._USER_INFO_KEY)) || forceClear === true) {
      const payload = {
        input: {
          name: userName,
        },
      };
      const userInfo = await myProfileAPI.getProfile(payload);
      this.userInfo = userInfo;
      sessionStorage.setItem(this._USER_INFO_KEY, JSON.stringify(userInfo));
    }
  }

  private logoutSSO(name) {
    return iamAPI.logoutIDP(name);
  }

  private createProjectList(projects: any[]) {
    // ignore admin project which is predefine by contrail
    projects = projects.filter(p => p.name === 'default-project' || p.scope != null);
    projects = projects.map(p => {
      return {
        name: p.name,
        id: p.id,
        scope: p.scope,
        parent_project_id: p.parent_project_id,
        customer_uuid: p.customer_uuid,
      };
    });

    // make default-project first
    projects.sort((a, b) => {
      if (a.name === 'default-project') {
        return -1;
      } else if (b.name === 'default-project') {
        return 1;
      } else {
        if (a.scope === 'OpCo') {
          return -1;
        } else if (b.scope === 'OpCo') {
          return 1;
        }
      }
      return a.name > b.name ? 1 : -1;
    });
    return projects;
  }

  private getTokenAuthPayload(token, project) {
    return {
      auth: {
        identity: {
          methods: ['token'],
          token: {
            id: token,
          },
        },
        scope: {
          project: {
            domain: {
              id: 'default',
            },
            name: project,
          },
        },
      },
    };
  }

  private getPasswordAuthPayload(username, password, project) {
    const authModel: any = {
      auth: {
        identity: {
          methods: ['password'],
          password: {
            user: {
              domain: {
                id: 'default',
              },
              name: username,
              password,
            },
          },
        },
      },
    };

    if (project) {
      authModel.auth.scope = {
        project: {
          domain: {
            id: 'default',
          },
          name: project,
        },
      };
    }
    return authModel;
  }

  private getPasswordTotpAuthPayload(username, password, passcode, project) {
    const authModel: any = {
      auth: {
        identity: {
          methods: ['password', 'totp'],
          password: {
            user: {
              domain: {
                id: 'default',
              },
              name: username,
              password,
            },
          },
          totp: {
            user: {
              name: username,
              passcode,
              domain: {
                id: 'default',
              },
            },
          },
        },
      },
    };

    if (project) {
      authModel.auth.scope = {
        project: {
          domain: {
            id: 'default',
          },
          name: project,
        },
      };
    }
    return authModel;
  }

  private getPasswordEmailAuthPayload(username, password, passcode, project) {
    const authModel: any = {
      auth: {
        identity: {
          methods: ['password', 'email'],
          password: {
            user: {
              domain: {
                id: 'default',
              },
              name: username,
              password,
            },
          },
          email: {
            user: {
              name: username,
              passcode,
              domain: {
                id: 'default',
              },
            },
          },
        },
      },
    };

    if (project) {
      authModel.auth.scope = {
        project: {
          domain: {
            id: 'default',
          },
          name: project,
        },
      };
    }
    return authModel;
  }

  private async saveSSEToken() {
    try {
      const resp = await sseAPI.getSseToken();
      const sseData = ensureJson(resp.body);
      const sseToken = sseData.random_str;
      this.setSseToken('?sse_token=' + encodeURIComponent(sseToken));
    } catch (ex) {
      this.setSseToken(this.getToken());
    }
  }

  private async getUserCapabilities() {
    const r = await iamAPI.getUserCapabilities();
    const result = ensureJson(r.body);
    this.setCapabilities(result.output.capabilities);
  }

  private saveLoginScope(result) {
    const roleScope = result.token.roles[0].role_scope;
    if (roleScope !== 'Enterprise') {
      this.setLoginRoleScope(roleScope);
    }
  }

  private clearUserInfo() {
    this.userInfo = null;
    sessionStorage.removeItem(this._USER_INFO_KEY);
  }

  private async v3Authentication(auth, handle401: boolean, authScope?: string) {
    try {
      const authResult = await iamAPI.authenticate(auth, handle401);
      const token = authResult.responseHeader['x-subject-token'];
      const result = authResult.body;
      // scoped token
      if (result.token.project) {
        if (authScope) {
          if (result.token.project.scope !== authScope) {
            throw new Error('Authentication Failed');
          }
        }
        this.saveLoginScope(result);
        await this.saveScopedToken(token, result);
        this.saveUserInfo(result.token.user.name);
        const authInfo = new DefaultAuthenticationInfo(
          this.getUserName(),
          this.getUserId(),
          result.token.project.name,
          result.token.project.id
        );
        await getProviderFactory()
          .getPreferenceProvider()
          .fetchPreference(authInfo);
        return authInfo;
      } else {
        this.clearUserInfo();
        // get the accessable project list
        if (auth.auth.identity.password) {
          return this.getProjectListAndAuthenticate(token, auth.auth.identity.password.user.name);
        } else {
          return this.getProjectListAndAuthenticate(token);
        }
      }
    } catch (error) {
      if (error instanceof SBusinessError) {
        if (!error.message) {
          if (error.status === 401) {
            if (auth.auth.identity.methods.length === 2) {
              error.message = 'Login failed. Check your username, password and verification code.';
            } else {
              error.message = 'Login failed. Check your username and password.';
            }
          } else {
            error.message = 'Login failed (' + error.status + ').';
          }
        }
        throw error;
      } else {
        throw new SBusinessError(500, null, 'Login failed.');
      }
    }
  }

  private async getProjectListAndAuthenticate(token, userName?: string) {
    const r = await iamAPI.getProjects({ 'X-Auth-Token': token });
    const result = r.body;
    const projects = this.createProjectList(result.projects);
    this.setProjects(projects);
    if (projects.length) {
      // cache to speed up the login process.
      if (userName) {
        localStorage.setItem(userName + '_default_project', projects[0].name);
      }
      const auth = this.getTokenAuthPayload(token, projects[0].name);
      return this.v3Authentication(auth, true, null);
    } else {
      throw new Error('Project list is empty');
    }
  }
}

export interface IProject {
  display_name?: any;
  id: string;
  text: string;
  parent_project_id?: string;
  name: string;
  scope: string;
  customer_uuid?: string;
  pid?: string;
}

export class DefaultAuthenticationInfo implements IAuthenticationInfo {
  public userName: string;
  public userId: string;
  public project: string;
  public projectId: string;
  constructor(userName: string, userId: string, project: string, projectId: string) {
    this.userName = userName;
    this.userId = userId;
    this.project = project;
    this.projectId = projectId;
  }
}
